import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { useNotification } from '../../context/NotificationContext';
import { getStylists, getAdminServices, getAdminCategories, createStylist, updateStylist, deleteStylist } from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import BrandBranchFilterFields from '../../components/BrandBranchFilterFields';
import { normalizeAdminListResponse } from '../../utils/adminListResponse';
import ExpandableText from '../../components/ExpandableText';
import usePortalScope from '../../hooks/usePortalScope';
import { removeConfirmMessage, deactivateConfirmMessage } from '../../utils/portalCopy';
import { tenantListParams } from '../../utils/contentScope';
import { getTenDigitPhoneError, toTenDigitPhone } from '../../utils/phone';
import './AdminPages.css';

const StylistManagement = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const portal = usePortalScope();
  const salons = portal.salons;
  const brands = portal.brands;
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingStylist, setEditingStylist] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    brandId: portal.lockBrandId || '',
    salonId: portal.lockSalonId || ''
  });
  const [formData, setFormData] = useState({
    name: '',
    photo: '',
    experience: '',
    rating: '',
    specialization: '',
    bio: '',
    phone: '',
    email: '',
    brandId: portal.lockBrandId || '',
    salonId: portal.lockSalonId || '',
    categoryFilter: '',
    categories: [],
    services: []
  });
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const formRef = useRef(null);

  useEffect(() => {
    if (!portal.lockBrandId && !portal.lockSalonId) return;
    setFilters((prev) => {
      const brandId = portal.lockBrandId || prev.brandId || '';
      const salonId = portal.lockSalonId || prev.salonId || '';
      if (prev.brandId === brandId && prev.salonId === salonId) return prev;
      return { ...prev, brandId, salonId };
    });
  }, [portal.lockBrandId, portal.lockSalonId]);

  const searchKey = useMemo(
    () => JSON.stringify({ searchTerm, ...filters }),
    [searchTerm, filters]
  );

  const getStylistParams = useCallback(() => {
    const params = { ...tenantListParams(portal, filters) };
    if (searchTerm) params.search = searchTerm;
    return params;
  }, [searchTerm, filters, portal]);

  const { items: stylists, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: getStylists,
    resetKey: searchKey,
    getParams: getStylistParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(stylists, (stylist) => stylist._id, {
    defaultView: 'grid',
    resetKey: searchKey
  });

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getAdminCategories({ all: true });
      const { data } = normalizeAdminListResponse(response);
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const servicesFilteredByCategory = formData.categoryFilter
    ? services.filter((s) => {
        const cid = s.category?._id || s.category;
        return cid != null && String(cid) === String(formData.categoryFilter);
      })
    : services;

  const fetchServices = async () => {
    try {
      const response = await getAdminServices({ all: true });
      const { data } = normalizeAdminListResponse(response);
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!String(formData.name || '').trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.brandId && !portal.lockBrandId) {
      errors.brandId = 'Select a brand';
    }
    if (!formData.salonId) {
      errors.salonId = 'Select a branch';
    }
    const phoneErr = getTenDigitPhoneError(formData.phone);
    if (phoneErr) {
      errors.phone = phoneErr;
    }
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      showError('Please fix the highlighted fields and try again.');
      formRef.current?.querySelector('.input-error')?.focus?.();
      return;
    }
    setFormErrors({});
    try {
      const { brandId, salonId, categoryFilter, ...rest } = formData;
      const payload = {
        ...rest,
        salons: salonId ? [salonId] : [],
        experience: parseInt(formData.experience) || 0,
        rating: parseFloat(formData.rating) || 0
      };

      if (editingStylist) {
        await updateStylist(editingStylist._id, payload);
      } else {
        await createStylist(payload);
      }
      setShowForm(false);
      setEditingStylist(null);
      resetForm();
      showSuccess(editingStylist ? 'Stylist updated.' : 'Stylist created.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || `Failed to ${editingStylist ? 'update' : 'create'} stylist`);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      photo: '',
      experience: '',
      rating: '',
      specialization: '',
      bio: '',
      phone: '',
      email: '',
      brandId: portal.lockBrandId || '',
      salonId: portal.lockSalonId || '',
      categoryFilter: '',
      categories: [],
      services: []
    });
    setShowCategoryDropdown(false);
    setShowServiceDropdown(false);
    setFormErrors({});
  };

  const formatSalonLabel = (salon) => {
    if (!salon) return '';
    const brand = salon.brand?.name || 'N/A';
    const branch = [salon.name, salon.branch].filter(Boolean).join(' ');
    return `${brand} – ${branch}`;
  };

  const handleEdit = (stylist) => {
    const salonId = stylist.salons?.[0]?._id || stylist.salons?.[0] || '';
    const selectedSalon = salons.find((s) => String(s._id) === String(salonId));
    const brandId = selectedSalon?.brand?._id || selectedSalon?.brand || '';
    setEditingStylist(stylist);
    setFormData({
      name: stylist.name,
      photo: stylist.photo || '',
      experience: stylist.experience || '',
      rating: stylist.rating || '',
      specialization: stylist.specialization || '',
      bio: stylist.bio || '',
      phone: toTenDigitPhone(stylist.phone),
      email: stylist.email || '',
      brandId: brandId ? String(brandId) : '',
      salonId: salonId ? String(salonId) : '',
      categoryFilter: '',
      categories: stylist.categories?.map(c => c._id || c) || [],
      services: stylist.services?.map(s => s._id || s) || []
    });
    setShowCategoryDropdown(false);
    setShowServiceDropdown(false);
    setShowForm(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      brandId: portal.lockBrandId || '',
      salonId: portal.lockSalonId || ''
    });
  };

  const toggleCategorySelection = (categoryId) => {
    const id = String(categoryId);
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.map(String).includes(id)
        ? prev.categories.filter((c) => String(c) !== id)
        : [...prev.categories, id]
    }));
  };

  const toggleServiceSelection = (serviceId) => {
    const id = String(serviceId);
    setFormData((prev) => ({
      ...prev,
      services: prev.services.map(String).includes(id)
        ? prev.services.filter((s) => String(s) !== id)
        : [...prev.services, id]
    }));
  };

  const getSelectedCategoryNames = () => {
    if (formData.categories.length === 0) return 'Select categories...';
    const activeCategories = (categories || []).filter((c) => c.isActive !== false);
    const selected = activeCategories.filter((c) => formData.categories.map(String).includes(String(c._id)));
    if (selected.length <= 2) {
      return selected.map((c) => c.name).join(', ');
    }
    return `${selected.length} categories selected`;
  };

  const getSelectedServiceNames = () => {
    if (formData.services.length === 0) return 'Select services...';
    const selected = servicesFilteredByCategory.filter((s) => formData.services.map(String).includes(String(s._id)));
    if (selected.length <= 2) {
      return selected.map((s) => s.name).join(', ');
    }
    return `${selected.length} services selected`;
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Remove stylist',
      message: removeConfirmMessage('stylist'),
      confirmText: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await deleteStylist(id);
      showSuccess('Stylist removed.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove stylist');
    }
  };

  const handleToggleActive = async (stylist) => {
    const nextActive = stylist.isActive === false;
    if (!nextActive) {
      const ok = await confirm({
        title: 'Deactivate stylist',
        message: deactivateConfirmMessage('stylist'),
        confirmText: 'Deactivate',
        danger: true
      });
      if (!ok) return;
    }
    try {
      await updateStylist(stylist._id, {
        isActive: nextActive
      });
      showSuccess(nextActive ? 'Stylist activated.' : 'Stylist deactivated.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update stylist');
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalf = (rating || 0) % 1 >= 0.5;
    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="star filled">★</span>);
    }
    if (hasHalf) stars.push(<span key="half" className="star half">★</span>);
    for (let i = stars.length; i < 5; i++) {
      stars.push(<span key={i} className="star">☆</span>);
    }
    return stars;
  };

  const stylistColumns = useMemo(
    () => [
      { key: 'name', label: 'Name', render: (stylist) => <strong>{stylist.name}</strong> },
      {
        key: 'specialization',
        label: 'Specialization',
        render: (stylist) => stylist.specialization || '-'
      },
      {
        key: 'experience',
        label: 'Experience',
        render: (stylist) => `${stylist.experience || 0} yrs`
      },
      {
        key: 'rating',
        label: 'Rating',
        render: (stylist) => (
          <span className="stylist-rating">
            {renderStars(stylist.rating)} ({stylist.rating || 0})
          </span>
        )
      },
      {
        key: 'status',
        label: 'Status',
        render: (stylist) => (
          <span className={`status-badge ${stylist.isActive !== false ? 'active' : 'inactive'}`}>
            {stylist.isActive !== false ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        key: 'branch',
        label: 'Branch',
        render: (stylist) =>
          stylist.salons?.length > 0
            ? stylist.salons.map((s) => formatSalonLabel(s)).join(', ')
            : '-'
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (stylist) => (
          <div className="table-actions">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => handleToggleActive(stylist)}
            >
              {stylist.isActive !== false ? 'Deactivate' : 'Activate'}
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => handleEdit(stylist)}>
              Edit
            </button>
            <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(stylist._id)}>
              Remove
            </button>
          </div>
        )
      }
    ],
    []
  );

  const stylistListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && stylists.length > 0 && (
        <p className="admin-infinite-end">All stylists loaded ({pagination.total} total).</p>
      )}
    </>
  );

  const stylistEmptyState = (
    <div className="empty-state">
      <p>No stylists found. Add your first stylist!</p>
    </div>
  );

  return (
    <AdminLayout
      pageTitle="Stylist Management"
      subtitle={
        pagination.total
          ? `${stylists.length} of ${pagination.total} stylists · scroll for more`
          : 'Manage stylists and their profiles'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header admin-page-header--split">
          <div className="admin-page-header-title-row">
            <h1 style={{ margin: 0 }}>Stylist List</h1>
            <input
              type="text"
              className="admin-search-input"
              placeholder="Search stylists by name, specialization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setEditingStylist(null);
              resetForm();
              setShowForm(true);
            }}
          >
            + Add Stylist
          </button>
        </div>

        {(portal.showBrandPicker || portal.showBranchPicker) && (
        <div className="admin-card filter-card">
          <h3>Filters</h3>
          <div className="filter-grid">
            <BrandBranchFilterFields
              portal={portal}
              filters={filters}
              onChange={handleFilterChange}
              brands={brands}
              salons={salons}
            />
            <div className="form-group filter-actions">
              <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>
        )}

        {loading ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
        {showForm && (
          <div className="admin-card stylist-editor-form" style={{ marginBottom: '24px' }}>
            <h2>{editingStylist ? 'Edit Stylist' : 'Add New Stylist'}</h2>
            <p style={{ color: '#8189A5', fontSize: '12px', marginBottom: '16px' }}>
              Hierarchy: Brand → Branch → Stylist. Each stylist must be assigned to one branch.
            </p>
            <form ref={formRef} onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    maxLength={250}
                    className={formErrors.name ? 'input-error' : undefined}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: '' }));
                    }}
                    required
                  />
                  {formErrors.name && <span className="form-error-text">{formErrors.name}</span>}
                  <small style={{ color: '#8189A5', fontSize: '12px' }}>
                    Maximum 250 characters (not words).
                  </small>
                </div>
                <div className="form-group">
                  <label>Photo URL</label>
                  <input
                    type="url"
                    value={formData.photo}
                    onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                    placeholder="https://example.com/photo.jpg"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Experience (years)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Rating (0-5)</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Specialization</label>
                <input
                  type="text"
                  value={formData.specialization}
                  onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                  placeholder="e.g. Hair Color, Cuts"
                />
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    className={formErrors.phone ? 'input-error' : ''}
                    onChange={(e) => {
                      const digits = toTenDigitPhone(e.target.value);
                      setFormData({ ...formData, phone: digits });
                      if (formErrors.phone) setFormErrors((prev) => ({ ...prev, phone: '' }));
                    }}
                    maxLength={10}
                    inputMode="numeric"
                    placeholder="10-digit mobile"
                  />
                  {formErrors.phone && <span className="form-error-text">{formErrors.phone}</span>}
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Brand *</label>
                  <select
                    value={formData.brandId}
                    className={formErrors.brandId ? 'input-error' : undefined}
                    disabled={Boolean(portal.lockBrandId)}
                    onChange={(e) => {
                      setFormData((prev) => ({
                        ...prev,
                        brandId: e.target.value,
                        salonId: portal.lockSalonId || ''
                      }));
                      if (formErrors.brandId) setFormErrors((prev) => ({ ...prev, brandId: '' }));
                    }}
                    required
                  >
                    <option value="">Select Brand</option>
                    {brands.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}{b.brandCode ? ` (${b.brandCode})` : ''}
                      </option>
                    ))}
                  </select>
                  {formErrors.brandId && <span className="form-error-text">{formErrors.brandId}</span>}
                </div>
                <div className="form-group">
                  <label>Branch *</label>
                  <select
                    value={formData.salonId}
                    className={formErrors.salonId ? 'input-error' : undefined}
                    disabled={Boolean(portal.lockSalonId)}
                    onChange={(e) => {
                      const salonId = e.target.value;
                      const selected = salons.find((s) => String(s._id) === String(salonId));
                      const selectedBrandId = selected?.brand?._id || selected?.brand || '';
                      setFormData((prev) => ({
                        ...prev,
                        salonId,
                        brandId: prev.brandId || (selectedBrandId ? String(selectedBrandId) : '') || portal.lockBrandId || ''
                      }));
                      if (formErrors.salonId) setFormErrors((prev) => ({ ...prev, salonId: '' }));
                    }}
                    required
                  >
                    <option value="">Select Branch</option>
                    {salons
                      .filter((s) => !formData.brandId || String(s.brand?._id || s.brand) === String(formData.brandId))
                      .map((s) => (
                        <option key={s._id} value={s._id}>
                          {[s.name, s.branch].filter(Boolean).join(' ')}
                          {s.branchCode ? ` (${s.branchCode})` : ''}
                        </option>
                      ))}
                  </select>
                  {formErrors.salonId && <span className="form-error-text">{formErrors.salonId}</span>}
                  <small style={{ color: '#8189A5', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Stylists are branch-specific and used for mirror sessions at that location.
                  </small>
                </div>
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label>Categories (Stylist Specializations)</label>
                <div
                  className="dropdown-select-trigger"
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                  style={{
                    padding: '12px 16px',
                    background: '#1D1D1D',
                    border: '1px solid #363636',
                    borderRadius: '8px',
                    color: formData.categories.length > 0 ? '#FFFFFF' : '#5A5C62',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{getSelectedCategoryNames()}</span>
                  <span style={{ fontSize: '10px', opacity: 0.6 }}>{showCategoryDropdown ? '▲' : '▼'}</span>
                </div>
                {showCategoryDropdown && (
                  <div
                    className="dropdown-select-panel"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#1D1D1D',
                      border: '1px solid #363636',
                      borderRadius: '8px',
                      marginTop: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 5000
                    }}
                  >
                    {(categories || []).filter((c) => c.isActive !== false).length === 0 ? (
                      <div style={{ padding: '12px 16px', color: '#8189A5' }}>No categories available</div>
                    ) : (
                      (categories || []).filter((c) => c.isActive !== false).map((cat) => (
                        <label
                          key={cat._id}
                          className="admin-checkbox-label"
                          style={{
                            display: 'flex',
                            padding: '10px 16px',
                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="admin-checkbox-input"
                            checked={formData.categories.map(String).includes(String(cat._id))}
                            onChange={() => toggleCategorySelection(cat._id)}
                          />
                          <span>{cat.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
                <small style={{ color: '#8189A5', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Assign categories this stylist specializes in
                </small>
              </div>

              <div className="form-group">
                <label>Filter Services by Category</label>
                <select
                  value={formData.categoryFilter}
                  onChange={(e) => setFormData({ ...formData, categoryFilter: e.target.value })}
                >
                  <option value="">All categories</option>
                  {(categories || []).filter((c) => c.isActive !== false).map((cat) => (
                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                  ))}
                </select>
                <small style={{ color: '#8189A5', fontSize: '12px' }}>Optional: Filter the service list below</small>
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label>Services</label>
                <div
                  className="dropdown-select-trigger"
                  onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                  style={{
                    padding: '12px 16px',
                    background: '#1D1D1D',
                    border: '1px solid #363636',
                    borderRadius: '8px',
                    color: formData.services.length > 0 ? '#FFFFFF' : '#5A5C62',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <span>{getSelectedServiceNames()}</span>
                  <span style={{ fontSize: '10px', opacity: 0.6 }}>{showServiceDropdown ? '▲' : '▼'}</span>
                </div>
                {showServiceDropdown && (
                  <div
                    className="dropdown-select-panel"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: '#1D1D1D',
                      border: '1px solid #363636',
                      borderRadius: '8px',
                      marginTop: '4px',
                      maxHeight: '240px',
                      overflowY: 'auto',
                      zIndex: 5000
                    }}
                  >
                    {servicesFilteredByCategory.length === 0 ? (
                      <div style={{ padding: '12px 16px', color: '#8189A5' }}>No services available</div>
                    ) : (
                      servicesFilteredByCategory.map((s) => (
                        <label
                          key={s._id}
                          className="admin-checkbox-label"
                          style={{
                            display: 'flex',
                            padding: '10px 16px',
                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="admin-checkbox-input"
                            checked={formData.services.map(String).includes(String(s._id))}
                            onChange={() => toggleServiceSelection(s._id)}
                          />
                          <span style={{ flex: 1 }}>
                            {s.name}
                            <span style={{ color: '#8189A5', marginLeft: '8px', fontSize: '12px' }}>
                              ₹{s.price}, {s.duration} min{s.category?.name ? ` (${s.category.name})` : ''}
                            </span>
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                )}
                <small style={{ color: '#8189A5', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Click to expand and select services this stylist can perform
                </small>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingStylist ? 'Update Stylist' : 'Create Stylist'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingStylist(null);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <AdminListViewControls
          viewMode={listView.viewMode}
          onViewModeChange={listView.setViewMode}
          selectedCount={listView.selectedCount}
          onSelectAll={listView.selectAll}
          onClearSelection={listView.clearSelection}
          allSelected={listView.allSelected}
          someSelected={listView.someSelected}
          totalCount={stylists.length}
            showSelection={false}
        />
        <AdminSelectableList
          items={stylists}
          getItemId={(stylist) => stylist._id}
          viewMode={listView.viewMode}
            selectable={false}
          isSelected={listView.isSelected}
          onToggleItem={listView.toggleItem}
          allSelected={listView.allSelected}
          someSelected={listView.someSelected}
          onSelectAll={listView.selectAll}
          onClearSelection={listView.clearSelection}
          columns={stylistColumns}
          tableClassName="stylist-table"
          listClassName="stylist-list"
          renderGridCard={(stylist) => (
            <div className="admin-card admin-card--stretch stylist-card">
              <div className="stylist-card-header">
                <div className="stylist-photo">
                  {stylist.photo ? (
                    <img src={stylist.photo} alt={stylist.name} />
                  ) : (
                    <div className="stylist-placeholder">👤</div>
                  )}
                </div>
                <div className="stylist-info">
                  <h3>{stylist.name}</h3>
                  {stylist.specialization && (
                    <p className="stylist-specialization">
                      <ExpandableText text={stylist.specialization} lines={1} />
                    </p>
                  )}
                  <div className="stylist-stats">
                    <span>{stylist.experience || 0} yrs exp</span>
                    <span className="stylist-rating">
                      {renderStars(stylist.rating)} ({stylist.rating || 0})
                    </span>
                  </div>
                </div>
                <div className="card-actions">
                  <span className={`status-badge ${stylist.isActive !== false ? 'active' : 'inactive'}`}>
                    {stylist.isActive !== false ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleToggleActive(stylist)}
                  >
                    {stylist.isActive !== false ? 'Deactivate' : 'Activate'}
                  </button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={() => handleEdit(stylist)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDelete(stylist._id)}>
                    Remove
                  </button>
                </div>
              </div>
              {(stylist.bio || stylist.phone || stylist.email) && (
                <div className="stylist-details">
                  {stylist.bio && (
                    <p>
                      <ExpandableText text={stylist.bio} lines={3} />
                    </p>
                  )}
                  {stylist.phone && (
                    <p>
                      📞 <ExpandableText text={stylist.phone} lines={1} minCharsForToggle={30} />
                    </p>
                  )}
                  {stylist.email && (
                    <p>
                      ✉️ <ExpandableText text={stylist.email} lines={1} minCharsForToggle={30} />
                    </p>
                  )}
                </div>
              )}
              {(stylist.salons?.length > 0 || stylist.categories?.length > 0 || stylist.services?.length > 0) && (
                <div className="stylist-links">
                  {stylist.salons?.length > 0 && (
                    <div>
                      <strong>Branch:</strong>{' '}
                      <ExpandableText
                        text={stylist.salons.map((s) => formatSalonLabel(s)).join(', ')}
                        lines={2}
                      />
                    </div>
                  )}
                  {stylist.categories?.length > 0 && (
                    <div>
                      <strong>Categories:</strong>{' '}
                      <ExpandableText text={stylist.categories.map((c) => c.name).join(', ')} lines={2} />
                    </div>
                  )}
                  {stylist.services?.length > 0 && (
                    <div>
                      <strong>Services:</strong>{' '}
                      <ExpandableText text={stylist.services.map((s) => s.name).join(', ')} lines={2} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          emptyState={stylistEmptyState}
          scrollRef={scrollRef}
          footer={stylistListFooter}
        />
        </>
        )}
      </div>
    </AdminLayout>
  );
};

export default StylistManagement;
